import {
  memo,
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'antd';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { QuickBetBaseSwitcher } from 'newelements/_Betslip/components/shared/Switchers/QuickBetSwitcher/BaseSwtcher';
import { toggleBetslip } from 'newelements/_Betslip/managers/ToggleManager/toggle-betslip-options';
import { FooterContainer, Header } from 'newelements/_Betslip/views/style';
import { CustomKeyboard } from 'components/CustomKeyboard';
import { Popup } from 'components/Popup';
import { StakeField } from 'components/StakeField';
import { BetWithBonusOrDepositBalance } from 'newcomponents/Shared/betslip/BetWithBonusOrDepositBalance';
import { useAccountBalanceAvailable } from 'hooks/useCheckIsAccountBalanceAvailable';
import { useCheckStakeBalance } from 'hooks/useCheckStakeBalance';
import { updateQuickBetStake } from 'store/actions/betslip';
import {
  getBetslipOpen,
  getQuickBetEnabled,
  getQuickBetStake,
  getUseBonusBalance
} from 'store/selectors/betslip';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

const { BonusBalanceSwitcher } = lazy(
  () =>
    import(
      /* webpackChunkName: "betslip-bonus-balance-switcher" */ 'newcomponents/Shared/betslip/BonusBalanceSwitcher'
    )
);

export const QuickBetComponent = memo((): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isBetslipOpened = useSelector(getBetslipOpen);
  const quickBetStake = useSelector(getQuickBetStake);
  const isQuickBetEnabled = useSelector(getQuickBetEnabled);
  const useBonusBalance = useSelector(getUseBonusBalance);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const user = useSelector(getUser);

  const checkStakeBalance = useCheckStakeBalance();
  const accountBalanceAvailable = useAccountBalanceAvailable();

  const [stake, setStake] = useState(`${quickBetStake || ''}`);
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  useEffect(() => {
    checkStakeBalance('betslip-wrapper', stake || 0, (disable: boolean) =>
      setIsDisabled(disable)
    );
  }, [stake, useBonusBalance]);

  const showQuickBetslip = isBetslipOpened && isQuickBetEnabled;

  const onSave = useCallback(() => {
    toggleBetslip();
    setVisible(false);

    if (quickBetStake !== Number(stake)) {
      dispatch(updateQuickBetStake(Number(stake)));
    }
  }, [quickBetStake, stake]);

  return (
    <Popup
      animationType="toTop"
      open={showQuickBetslip}
      className="quickBetPopup"
    >
      <Header>
        <QuickBetBaseSwitcher
          tooltip
          className="betslipFeatureSwitcher"
          isActive={showQuickBetslip}
        />
      </Header>
      <Form form={form}>
        <StakeField
          checkCurrencyContent={isLoggedIn}
          stake={stake}
          inputDefaultValue={stake}
          onClick={() => setVisible(true)}
          placeholder={t('betslip.stake')}
          onChange={value => setStake(value)}
          active={visible}
        />
        {visible && (
          <CustomKeyboard
            onChange={setStake}
            value={stake}
            size="middle"
            toggleQuickBet={setVisible}
            scrollIntoView
            predefinedStakesFree={!SpringConfigs.PREDEFINE_STAKES_ENABLE}
          />
        )}

        {accountBalanceAvailable ? (
          SpringConfigs.BET_WITH_ONLY_BONUS_OR_DEPOSIT || user.bonus_money ? (
            <Suspense>
              <BetWithBonusOrDepositBalance />
            </Suspense>
          ) : (
            <Suspense>
              <BonusBalanceSwitcher />
            </Suspense>
          )
        ) : null}

        <FooterContainer style={{ background: 'var(--v3-black-0)' }}>
          <Button
            size="large"
            type="primary"
            disabled={isDisabled}
            onClick={onSave}
          >
            {t('betslip.save')}
          </Button>
        </FooterContainer>
      </Form>
    </Popup>
  );
});
